import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { IRating, IRatingSubmission } from "../../app/interfaces/IRating";
import { IQueryParams, ISortableHeader } from "../../app/interfaces/ISortableTable";
import { IVendor } from "../../app/interfaces/IVendor";
import { getVendorRatingsThunk, selectVendorDetailState, ratingsSelector, deleteVendorRatingThunk, updateVendorRatingThunk } from "../../slices/vendorDetailSlice";
import { selectUser } from "../../slices/userSlice";
import VendorRatingStars, { StarSize } from "../stars/VendorRatingStars";
import SortableTable from "../tables/SortableTable";
import styled from "styled-components";
import { Flex, FormFieldAPI, Label, Popover, Table, Tile,  } from "@procore/core-react";
import { Info } from '@procore/core-icons'
import { ProcoreEmptyCell, ProcoreGenericCell, ProcoreLinkCell, ProcoreTextCell } from "../tables/ProcoreTableCells";
import { ProcoreVendorRatingStars } from "../stars/ProcoreVendorRatingStars";
import { VendorRatingsCategories } from "./VendorRatingsCategories";
import { Link } from "react-router-dom";
import { StyledProcoreTile } from "../CommonStyledComponents";
import { EditRatingModal } from "./EditRatingModal";
import { DeleteRatingModal } from "./DeleteRatingModal";
import { Icon } from "semantic-ui-react";

/**
 *
 * TODO:
 * - fix the star string parsing issue with .fixed()
 */


export const VendorRatings = ({ companyId, vendor }: {companyId: number, vendor: IVendor}) => {
  const dispatch = useAppDispatch();
  const { queryState } = useAppSelector(selectVendorDetailState);
  const user = useAppSelector(selectUser);
  const isAdmin = user?.companies?.find(c => c.id === companyId)?.isAdmin === true;

  const [editingRating, setEditingRating] = useState<IRating | null>(null);
  const [deletingRating, setDeletingRating] = useState<IRating | null>(null);

  const queryRatings = (queryParams?: IQueryParams) => {
    const vendorId = vendor.id;
    dispatch(getVendorRatingsThunk({companyId, vendorId, queryParams }));
  };

  const handleDeleteConfirm = () => {
    if (deletingRating) {
      dispatch(deleteVendorRatingThunk({ companyId, vendorId: vendor.id, ratingId: deletingRating.id }));
      setDeletingRating(null);
    }
  };

  const handleEditSave = (ratingValue: number, comment: string | null) => {
    if (editingRating) {
      const updatedRating: IRatingSubmission = {
        rating: ratingValue,
        comment: comment,
        customRatings: undefined,
      };
      dispatch(updateVendorRatingThunk({ companyId, vendorId: vendor.id, ratingId: editingRating.id, rating: updatedRating }));
      setEditingRating(null);
    }
  };

  const columnHeaders: ISortableHeader[] = [
    { value: 'rating', key: 'rating', text: 'Rating', sortable: true },
    { value: 'createdByEmail', key: 'createdByEmail', text: 'User', sortable: true },
    { value: 'created_date', key: 'created_date', text: 'Date', sortable: true },
    { value: 'description', key: 'description', text: 'Comments', sortable: false },
    { value: 'projectName', key: 'projectId', text: 'Project', sortable: true },
    { value: 'commitmentName', key: 'commitmentProcoreId', text: 'Commitment $', sortable: true },
    ...(isAdmin ? [{ value: 'actions', key: 'actions', text: '', sortable: false }] : []),
  ];

  const renderRatingCard = (dataItem: any) => {
    const rating = dataItem as IRating;
    const ratingDate = new Date(rating.createdDate).toLocaleDateString();

    const fakeRatingField = {
        input: {
            value: rating.rating ? rating.rating : 0
        },
        meta: {
            disabled: true
        },
        helpers: { }
    } as FormFieldAPI<number>;
    return (
        <StyledProcoreTile orientation="portrait">
            <Tile.Content>
                <Flex justifyContent="space-between">

                    <div>
                        <ProcoreVendorRatingStars
                            field={fakeRatingField}
                            size={StarSize.Medium}
                        />
                    </div>

                    <Flex alignItems="center" gap="8px">
                        <div>{ratingDate}</div>
                        {isAdmin && (
                            <StyledAdminActions>
                                <Icon
                                    name='pencil'
                                    link
                                    title='Edit rating'
                                    onClick={() => setEditingRating(rating)}
                                />
                                <Icon
                                    name='trash'
                                    link
                                    color='red'
                                    title='Delete rating'
                                    onClick={() => setDeletingRating(rating)}
                                />
                            </StyledAdminActions>
                        )}
                    </Flex>
                </Flex>

                {
                    (rating.createdByFirstName || rating.createdByEmail) &&
                        <div>
                            <Label>Reviewer:&nbsp; &nbsp;</Label>
                            {
                                rating.createdByFirstName ?
                                <a href={"mailto:"+rating.createdByEmail}>{rating.createdByFirstName} {rating.createdByLastName}</a> :
                                <a href={"mailto:"+rating.createdByEmail}>{rating.createdByEmail}</a>
                            }
                        </div>

                }



                {
                rating.projectId &&
                <div>
                    <Label>Project:&nbsp; &nbsp;</Label>

                    <Link to={`/companies/${companyId}/projects/${rating.projectId}`} >
                        {rating.projectName || ''}
                    </Link>
                </div>
            }

            {
                rating.commitmentProcoreId &&
                <div>
                    <Label>Commitment:&nbsp;</Label>

                    <Link to={`/companies/${companyId}/projects/${rating.commitmentProcoreId}`}>
                    {`${rating.commitmentName} (${rating.commitmentAmount ? '$' + rating.commitmentAmount!.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') : 'N/A'})`}
                    </Link>
                </div>

            }

            {
                rating.description &&
                <div>
                    <Label>Comments</Label>
                    <p>{rating.description}</p>
                </div>
            }

            {
                rating.customCategories?.length > 0 &&
                    <Flex gap="16px" wrap='wrap'>
                        <VendorRatingsCategories
                            categories={rating.customCategories}
                        />
                    </Flex>

            }

            </Tile.Content>
        </StyledProcoreTile>
    );

  };

  const renderRatingRow = (dataItem: any) => {
    const rating = dataItem as IRating;
    const ratingDate = new Date(rating.createdDate).toLocaleDateString();
    const fakeRatingField = {
        input: {
            value: rating.rating ? rating.rating : 0
        },
        meta: {
            disabled: true
        },
        helpers: { }
    } as FormFieldAPI<number>;

    return (
        <Table.BodyRow key={`rating-${rating.id}`}>
            <ProcoreGenericCell>
                <ProcoreVendorRatingStars
                    size={StarSize.Medium}
                    field={fakeRatingField}
                />
                {
                    rating.customCategories?.length > 0 &&
                    <Popover
                        key={rating.id}
                        trigger='hover'
                        overlay={
                            <StyledContent>
                                <VendorRatingsCategories
                                    categories={rating.customCategories}
                                />
                            </StyledContent>
                        }
                    >
                        {() => <Info size='sm' />}
                    </Popover>
                }
            </ProcoreGenericCell>
            <Table.BodyCell>
                <Table.TextCell>
                    {
                        rating.createdByFirstName ?
                        <a href={"mailto:"+rating.createdByEmail}>{rating.createdByFirstName} {rating.createdByLastName}</a> :
                        <a href={"mailto:"+rating.createdByEmail}>{rating.createdByEmail}</a>
                    }
                    {
                        rating.createdByJobTitle ?
                        <span>&nbsp;({rating.createdByJobTitle})</span> :
                        ''
                    }
                </Table.TextCell>
            </Table.BodyCell>
            <ProcoreTextCell text={ratingDate} />
            <ProcoreTextCell text={rating.description || ''} />
            {
                rating.projectId ?
                <ProcoreLinkCell
                    to={`/companies/${companyId}/projects/${rating.projectId}`}
                    cta={rating.projectName || ''}
                /> :
                <ProcoreEmptyCell />
            }
            {
                rating.commitmentProcoreId ?
                <ProcoreLinkCell
                    to={`/companies/${companyId}/projects/${rating.commitmentProcoreId}`}
                    cta={`${rating.commitmentName} (${rating.commitmentAmount ? '$' + rating.commitmentAmount!.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') : 'N/A'})`}
                /> :
                <ProcoreEmptyCell />
            }
            {
                isAdmin &&
                <Table.BodyCell>
                    <StyledAdminActions>
                        <Icon
                            name='pencil'
                            link
                            title='Edit rating'
                            onClick={() => setEditingRating(rating)}
                        />
                        <Icon
                            name='trash'
                            link
                            color='red'
                            title='Delete rating'
                            onClick={() => setDeletingRating(rating)}
                        />
                    </StyledAdminActions>
                </Table.BodyCell>
            }
        </Table.BodyRow>
    );
  }

  return (
      <>
          <div id='rating-info'>
              Average Rating:&nbsp;
              <VendorRatingStars
                  disabled
                  size={StarSize.Large}
                  stars={vendor.average_rating ? vendor.average_rating : 0}
                  displayNumericalValue
              />
              <div>Number of ratings: {vendor.total_ratings}</div>

          </div>
          <SortableTable
              dataSelector={ratingsSelector}
              headers={columnHeaders}
              renderDataCard={renderRatingCard}
              renderDataRow={renderRatingRow}
              queryData={queryRatings}
              queryState={queryState}
          />

          {editingRating && (
              <EditRatingModal
                  rating={editingRating}
                  open={!!editingRating}
                  onClose={() => setEditingRating(null)}
                  onSave={handleEditSave}
              />
          )}

          {deletingRating && (
              <DeleteRatingModal
                  open={!!deletingRating}
                  onClose={() => setDeletingRating(null)}
                  onConfirm={handleDeleteConfirm}
              />
          )}
      </>
  )
}

const StyledContent = styled(Popover.Content)`
  padding: 8px;
`;

const StyledAdminActions = styled.span`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  margin-left: 8px;
`;
